<template>
  <div class="container-fluid">


    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="row">
              <div class="col-md-6">
                <h5>Order Delivery Tracking</h5>
              </div>
              <div class="col-md-6 text-end">
                <button class="btn btn-success btn-sm" @click="exportToExcel">
                  <i class="fa fa-file-excel-o me-2"></i>Export to Excel
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Status</label>
                <select v-model="filters.status" class="form-select" @change="applyFilters">
                  <option value="">All Status</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Partially Delivered">Partially Delivered</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Category</label>
                <select v-model="filters.category" class="form-select" @change="applyFilters">
                  <option value="">All Categories</option>
                  <option value="Uniform">Uniform</option>
                  <option value="Material">Material</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Log Status</label>
                <select v-model="filters.logStatus" class="form-select" @change="applyFilters">
                  <option value="">All</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Search</label>
                <input 
                  v-model="searchQuery" 
                  type="text" 
                  class="form-control" 
                  placeholder="Search by log number, site..."
                  @input="applyFilters"
                />
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- Table -->
            <div v-else class="table-responsive">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Log Number</th>
                    <th>Tracking ID</th>
                    <th>Site</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Delivery Stats</th>
                    <th>Requisition Manager</th>
                    <th>Assigned To</th>
                    <th>Scheduled Date</th>
                    <th>Actual Date</th>
                    <th>Log Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredData.length === 0">
                    <td colspan="13" class="text-center">No records found</td>
                  </tr>
                  <tr v-for="item in paginatedData" :key="item.trackingId">
                    <td>{{ item.logNumber }}</td>
                    <td>
                      <span class="badge bg-info">{{ item.trackingId }}</span>
                    </td>
                    <td>{{ item.site?.name || '-' }}</td>
                    <td>
                      <span class="badge bg-secondary">{{ item.category }}</span>
                    </td>
                    <td>
                      <span :class="getPriorityBadgeClass(item.priority)">
                        {{ item.priority }}
                      </span>
                    </td>
                    <td>
                      <span :class="getStatusBadgeClass(item.status)">
                        {{ item.status }}
                      </span>
                    </td>
                    <td>
                      <div class="delivery-stats">
                        <small>
                          <strong>Total:</strong> {{ item.deliveryStats.totalEmployees }}<br/>
                          <strong>Delivered:</strong> {{ item.deliveryStats.deliveredEmployees }}<br/>
                          <strong>Pending:</strong> {{ item.deliveryStats.pendingEmployees }}<br/>
                          <strong>Progress:</strong> 
                          <span :class="getProgressClass(item.deliveryStats.completionPercentage)">
                            {{ item.deliveryStats.completionPercentage }}%
                          </span>
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{{ item.requisitionManager?.name || '-' }}</strong><br/>
                        <small class="text-muted">{{ item.requisitionManager?.code || '-' }}</small>
                      </div>
                    </td>
                    <td>
                      <div v-if="item.orderingAssignment?.assignedTo">
                        <strong>{{ item.orderingAssignment.assignedTo.name }}</strong><br/>
                        <small class="text-muted">{{ item.orderingAssignment.assignedTo.code }}</small>
                      </div>
                      <span v-else>-</span>
                    </td>
                    <td>{{ formatDate(item.scheduledDeliveryDate) }}</td>
                    <td>{{ formatDate(item.actualDeliveryDate) }}</td>
                    <td>
                      <span :class="getLogStatusBadgeClass(item.logStatus)">
                        {{ item.logStatus }}
                      </span>
                    </td>
                    <td>
                      <button 
                        class="btn btn-primary btn-sm" 
                        @click="viewDetails(item)"
                        title="View Details"
                      >
                        <i class="fa fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="row mt-3" v-if="filteredData.length > 0">
              <div class="col-md-6">
                <div class="dataTables_info">
                  Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredData.length }} entries
                </div>
              </div>
              <div class="col-md-6">
                <nav>
                  <ul class="pagination justify-content-end">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li 
                      class="page-item" 
                      v-for="page in totalPages" 
                      :key="page"
                      :class="{ active: page === currentPage }"
                    >
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Details Modal -->
    <div 
      class="custom-modal-overlay" 
      v-if="showDetailsModal"
      @click.self="closeDetailsModal"
    >
      <div class="custom-modal-container">
        <!-- Modal Header -->
        <div class="custom-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="header-text">
              <h2 class="header-title">Order Delivery Details</h2>
              <p class="header-subtitle">{{ selectedItem?.logNumber }}</p>
            </div>
          </div>
          <button class="custom-close-btn" @click="closeDetailsModal" type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="custom-modal-body" v-if="selectedItem">
          
          <!-- Status Overview Card -->
          <div class="status-overview-card">
            <div class="status-grid">
              <div class="status-item">
                <span class="status-label">Order Status</span>
                <span :class="['status-value', getStatusClass(selectedItem.status)]">
                  {{ selectedItem.status }}
                </span>
              </div>
              <div class="status-item">
                <span class="status-label">Log Status</span>
                <span :class="['status-value', getLogStatusClass(selectedItem.logStatus)]">
                  {{ selectedItem.logStatus }}
                </span>
              </div>
              <div class="status-item">
                <span class="status-label">Priority</span>
                <span :class="['status-value', getPriorityClass(selectedItem.priority)]">
                  {{ selectedItem.priority }}
                </span>
              </div>
              <div class="status-item">
                <span class="status-label">Category</span>
                <span class="status-value category">{{ selectedItem.category }}</span>
              </div>
            </div>
          </div>

          <!-- Delivery Progress Card -->
          <div class="delivery-progress-card">
            <h3 class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
              Delivery Progress
            </h3>
            <div class="progress-stats">
              <div class="progress-stat">
                <div class="stat-icon total">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-label">Total Employees</span>
                  <span class="stat-value">{{ selectedItem.deliveryStats.totalEmployees }}</span>
                </div>
              </div>
              <div class="progress-stat">
                <div class="stat-icon delivered">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-label">Delivered</span>
                  <span class="stat-value delivered">{{ selectedItem.deliveryStats.deliveredEmployees }}</span>
                </div>
              </div>
              <div class="progress-stat">
                <div class="stat-icon pending">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-label">Pending</span>
                  <span class="stat-value pending">{{ selectedItem.deliveryStats.pendingEmployees }}</span>
                </div>
              </div>
            </div>
            <div class="custom-progress-bar">
              <div class="progress-bar-track">
                <div 
                  class="progress-bar-fill" 
                  :class="getProgressFillClass(selectedItem.deliveryStats.completionPercentage)"
                  :style="{ width: selectedItem.deliveryStats.completionPercentage + '%' }"
                >
                  <span class="progress-label">{{ selectedItem.deliveryStats.completionPercentage }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Two Column Layout -->
          <div class="info-grid">
            
            <!-- Basic Information Card -->
            <div class="info-card">
              <h3 class="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Basic Information
              </h3>
              <div class="info-list">
                <div class="info-row">
                  <span class="info-key">Log Number</span>
                  <span class="info-value">{{ selectedItem.logNumber }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Tracking ID</span>
                  <span class="info-value tracking-id">{{ selectedItem.trackingId }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Order ID</span>
                  <span class="info-value">{{ selectedItem.orderId }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Expense Type</span>
                  <span class="info-value">{{ selectedItem.expenseType }}</span>
                </div>
              </div>
            </div>

            <!-- Site Information Card -->
            <div class="info-card">
              <h3 class="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                Site Information
              </h3>
              <div class="info-list">
                <div class="info-row">
                  <span class="info-key">Site Name</span>
                  <span class="info-value">{{ selectedItem.site?.name || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Site ID</span>
                  <span class="info-value">{{ selectedItem.site?.id || '-' }}</span>
                </div>
              </div>
            </div>

          </div>

          <!-- Team Section -->
          <div class="team-section">
            <h3 class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              Team Members
            </h3>
            <div class="team-grid">
              <div class="team-member">
                <div class="member-avatar requisition">
                  {{ getInitials(selectedItem.requisitionManager?.name) }}
                </div>
                <div class="member-info">
                  <span class="member-role">Requisition Manager</span>
                  <span class="member-name">{{ selectedItem.requisitionManager?.name || '-' }}</span>
                  <span class="member-code">{{ selectedItem.requisitionManager?.code || '-' }}</span>
                  <span class="member-email" v-if="selectedItem.requisitionManager?.email">
                    {{ selectedItem.requisitionManager.email }}
                  </span>
                </div>
              </div>
              <div class="team-member" v-if="selectedItem.orderingAssignment?.assignedTo">
                <div class="member-avatar assigned">
                  {{ getInitials(selectedItem.orderingAssignment.assignedTo.name) }}
                </div>
                <div class="member-info">
                  <span class="member-role">Assigned To</span>
                  <span class="member-name">{{ selectedItem.orderingAssignment.assignedTo.name }}</span>
                  <span class="member-code">{{ selectedItem.orderingAssignment.assignedTo.code }}</span>
                </div>
              </div>
              <div class="team-member" v-if="selectedItem.deliveryCompletedBy">
                <div class="member-avatar delivery">
                  {{ getInitials(selectedItem.deliveryCompletedBy.name) }}
                </div>
                <div class="member-info">
                  <span class="member-role">Delivered By</span>
                  <span class="member-name">{{ selectedItem.deliveryCompletedBy.name }}</span>
                  <span class="member-code">{{ selectedItem.deliveryCompletedBy.code }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline Section -->
          <div class="timeline-section">
            <h3 class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Timeline
            </h3>
            <div class="timeline">
              <div class="timeline-item">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Requisition Date</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.requisitionDate) }}</span>
                </div>
              </div>
              <div class="timeline-item" v-if="selectedItem.confirmation?.date">
                <div class="timeline-marker confirmed"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Confirmed</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.confirmation.date) }}</span>
                  <span class="timeline-status">{{ selectedItem.confirmation.status }}</span>
                </div>
              </div>
              <div class="timeline-item" v-if="selectedItem.orderPlacement?.placedDate">
                <div class="timeline-marker ordered"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Order Placed</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.orderPlacement.placedDate) }}</span>
                </div>
              </div>
              <div class="timeline-item" v-if="selectedItem.orderingCompletedOn">
                <div class="timeline-marker completed"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Ordering Completed</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.orderingCompletedOn) }}</span>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-marker scheduled"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Scheduled Delivery</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.scheduledDeliveryDate) }}</span>
                </div>
              </div>
              <div class="timeline-item" v-if="selectedItem.actualDeliveryDate">
                <div class="timeline-marker delivered"></div>
                <div class="timeline-content">
                  <span class="timeline-title">Actual Delivery</span>
                  <span class="timeline-date">{{ formatDate(selectedItem.actualDeliveryDate) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Remarks -->
          <div class="remarks-section" v-if="selectedItem.remarks">
            <h3 class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
              </svg>
              Remarks
            </h3>
            <div class="remarks-content">
              {{ selectedItem.remarks }}
            </div>
          </div>

          <!-- Cancellation Alert -->
          <div class="cancellation-alert" v-if="selectedItem.cancellation">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div class="alert-content">
              <span class="alert-title">Cancellation Details</span>
              <span class="alert-message">{{ selectedItem.cancellation }}</span>
            </div>
          </div>

        </div>

        <!-- Modal Footer -->
        <div class="custom-modal-footer">
          <button class="custom-btn secondary" @click="closeDetailsModal" type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Close
          </button>
          <button class="custom-btn primary" type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
            </svg>
            Print Details
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import * as XLSX from 'xlsx';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';

const toast = useToast();
const baseUrl = process.env.VUE_APP_BASE_URL;

// State
const allData = ref<any[]>([]);
const loading = ref(false);
const currentPage = ref(1);
const elementsPerPage = ref(25);
const searchQuery = ref('');
const filters = ref({
  status: '',
  category: '',
  logStatus: ''
});
const showDetailsModal = ref(false);
const selectedItem = ref<any>(null);

// Helper function to get auth token
const getAuthToken = () => localStorage.getItem('token');

// Format date helper function
function formatDate(dateString: any) {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

// Badge class helpers
function getStatusBadgeClass(status: string) {
  const classes: Record<string, string> = {
    'Delivered': 'badge bg-success',
    'Partially Delivered': 'badge bg-warning',
    'Pending': 'badge bg-secondary',
    'In Progress': 'badge bg-info'
  };
  return classes[status] || 'badge bg-secondary';
}

function getPriorityBadgeClass(priority: string) {
  const classes: Record<string, string> = {
    'High': 'badge bg-danger',
    'Medium': 'badge bg-warning',
    'Low': 'badge bg-info'
  };
  return classes[priority] || 'badge bg-secondary';
}

function getLogStatusBadgeClass(status: string) {
  return status === 'Open' ? 'badge bg-primary' : 'badge bg-dark';
}

function getProgressClass(percentage: number) {
  if (percentage === 100) return 'text-success fw-bold';
  if (percentage >= 50) return 'text-warning fw-bold';
  return 'text-danger fw-bold';
}

function getProgressBarClass(percentage: number) {
  if (percentage === 100) return 'bg-success';
  if (percentage >= 50) return 'bg-warning';
  return 'bg-danger';
}

// Custom modal helper functions
function getStatusClass(status: string) {
  const classes: Record<string, string> = {
    'Delivered': 'delivered',
    'Partially Delivered': 'partial',
    'Pending': 'pending',
    'In Progress': 'progress'
  };
  return classes[status] || 'pending';
}

function getPriorityClass(priority: string) {
  const classes: Record<string, string> = {
    'High': 'high',
    'Medium': 'medium',
    'Low': 'low'
  };
  return classes[priority] || 'medium';
}

function getLogStatusClass(status: string) {
  return status === 'Open' ? 'open' : 'closed';
}

function getProgressFillClass(percentage: number) {
  if (percentage === 100) return 'complete';
  if (percentage >= 50) return 'partial';
  return 'low';
}

function getInitials(name: string | undefined) {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// Fetch data
async function fetchOrderDeliveryData() {
  loading.value = true;
  try {
    const response = await fetch(`${baseUrl}api/admin/order-delivery-tracking-log`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      allData.value = result.data || [];
      toast.success(`Loaded ${result.count} order delivery records`);
    } else {
      toast.error('Failed to fetch order delivery data');
    }
  } catch (error) {
    console.error('Error fetching order delivery data:', error);
    toast.error('Error fetching order delivery data');
  } finally {
    loading.value = false;
  }
}

// Filtered data
const filteredData = computed(() => {
  let data = [...allData.value];

  // Apply status filter
  if (filters.value.status) {
    data = data.filter(item => item.status === filters.value.status);
  }

  // Apply category filter
  if (filters.value.category) {
    data = data.filter(item => item.category === filters.value.category);
  }

  // Apply log status filter
  if (filters.value.logStatus) {
    data = data.filter(item => item.logStatus === filters.value.logStatus);
  }

  // Apply search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    data = data.filter(item => 
      item.logNumber?.toLowerCase().includes(query) ||
      item.trackingId?.toLowerCase().includes(query) ||
      item.site?.name?.toLowerCase().includes(query) ||
      item.requisitionManager?.name?.toLowerCase().includes(query) ||
      item.orderingAssignment?.assignedTo?.name?.toLowerCase().includes(query)
    );
  }

  return data;
});

// Pagination
const totalPages = computed(() => Math.ceil(filteredData.value.length / elementsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * elementsPerPage.value);
const endIndex = computed(() => Math.min(startIndex.value + elementsPerPage.value, filteredData.value.length));

const paginatedData = computed(() => {
  return filteredData.value.slice(startIndex.value, endIndex.value);
});

function changePage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

function applyFilters() {
  currentPage.value = 1;
}

// Actions
function refreshData() {
  fetchOrderDeliveryData();
}

function viewDetails(item: any) {
  selectedItem.value = item;
  showDetailsModal.value = true;
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedItem.value = null;
}

// Export to Excel
function exportToExcel() {
  try {
    const dataToExport = filteredData.value.map(item => ({
      'Log Number': item.logNumber,
      'Tracking ID': item.trackingId,
      'Site': item.site?.name || '-',
      'Category': item.category,
      'Priority': item.priority,
      'Status': item.status,
      'Log Status': item.logStatus,
      'Total Employees': item.deliveryStats.totalEmployees,
      'Delivered': item.deliveryStats.deliveredEmployees,
      'Pending': item.deliveryStats.pendingEmployees,
      'Completion %': item.deliveryStats.completionPercentage,
      'Requisition Manager': item.requisitionManager?.name || '-',
      'Manager Code': item.requisitionManager?.code || '-',
      'Assigned To': item.orderingAssignment?.assignedTo?.name || '-',
      'Assignee Code': item.orderingAssignment?.assignedTo?.code || '-',
      'Scheduled Date': formatDate(item.scheduledDeliveryDate),
      'Actual Date': formatDate(item.actualDeliveryDate),
      'Requisition Date': formatDate(item.requisitionDate),
      'Expense Type': item.expenseType,
      'Remarks': item.remarks || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Order Delivery');
    
    const fileName = `order-delivery-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success('Data exported successfully');
  } catch (error) {
    console.error('Error exporting data:', error);
    toast.error('Error exporting data');
  }
}

// Lifecycle
onMounted(() => {
  fetchOrderDeliveryData();
});
</script>

<style scoped>
/* Base styles */
.delivery-stats {
  font-size: 0.85rem;
  line-height: 1.4;
}

.page-header {
  margin-bottom: 20px;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.badge {
  font-size: 0.8rem;
  padding: 0.35em 0.65em;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Custom Modal Overlay */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Modal Container */
.custom-modal-container {
  background: #ffffff;
  border-radius: 16px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Modal Header */
.custom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.header-subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
  font-weight: 500;
}

.custom-close-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s ease;
}

.custom-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Modal Body */
.custom-modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.custom-modal-body::-webkit-scrollbar {
  width: 8px;
}

.custom-modal-body::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.custom-modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-modal-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Section Title */
.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.section-title svg {
  color: #667eea;
}

/* Status Overview Card */
.status-overview-card {
  background: linear-gradient(135deg, #f6f8fb 0%, #e9eef5 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 16px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 8px;
  display: inline-block;
  width: fit-content;
}

.status-value.delivered {
  background: #dcfce7;
  color: #166534;
}

.status-value.partial {
  background: #fef3c7;
  color: #92400e;
}

.status-value.pending {
  background: #e0e7ff;
  color: #3730a3;
}

.status-value.progress {
  background: #dbeafe;
  color: #1e40af;
}

.status-value.open {
  background: #dbeafe;
  color: #1e40af;
}

.status-value.closed {
  background: #f3f4f6;
  color: #374151;
}

.status-value.high {
  background: #fee2e2;
  color: #991b1b;
}

.status-value.medium {
  background: #fed7aa;
  color: #9a3412;
}

.status-value.low {
  background: #ccfbf1;
  color: #115e59;
}

.status-value.category {
  background: #e9d5ff;
  color: #6b21a8;
}

/* Delivery Progress Card */
.delivery-progress-card {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.progress-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.progress-stat {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.total {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stat-icon.delivered {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.stat-icon.pending {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.stat-value.delivered {
  color: #10b981;
}

.stat-value.pending {
  color: #f59e0b;
}

/* Custom Progress Bar */
.custom-progress-bar {
  margin-top: 16px;
}

.progress-bar-track {
  background: #e5e7eb;
  height: 32px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.6s ease;
  position: relative;
  overflow: hidden;
}

.progress-bar-fill.complete {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.progress-bar-fill.partial {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.progress-bar-fill.low {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.progress-label {
  color: white;
  font-weight: 700;
  font-size: 14px;
  position: relative;
  z-index: 1;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

/* Info Card */
.info-card {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.info-row:last-child {
  border-bottom: none;
}

.info-key {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
  width: 40%;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  text-align: right;
  word-break: break-word;
}

.info-value.tracking-id {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
}

/* Team Section */
.team-section {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.team-member {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.member-avatar {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  color: white;
  flex-shrink: 0;
}

.member-avatar.requisition {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.member-avatar.assigned {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
}

.member-avatar.delivery {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.member-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.member-role {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.member-name {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.member-code {
  font-size: 13px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;
}

.member-email {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Timeline Section */
.timeline-section {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  padding-left: 32px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 12px;
  bottom: 12px;
  width: 2px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
}

.timeline-item {
  display: flex;
  gap: 16px;
  position: relative;
  padding: 12px 0;
}

.timeline-marker {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #667eea;
  border: 4px solid #ffffff;
  box-shadow: 0 0 0 2px #667eea;
  position: absolute;
  left: -32px;
  flex-shrink: 0;
  z-index: 1;
}

.timeline-marker.confirmed {
  background: #06b6d4;
  box-shadow: 0 0 0 2px #06b6d4;
}

.timeline-marker.ordered {
  background: #8b5cf6;
  box-shadow: 0 0 0 2px #8b5cf6;
}

.timeline-marker.completed {
  background: #f59e0b;
  box-shadow: 0 0 0 2px #f59e0b;
}

.timeline-marker.scheduled {
  background: #3b82f6;
  box-shadow: 0 0 0 2px #3b82f6;
}

.timeline-marker.delivered {
  background: #10b981;
  box-shadow: 0 0 0 2px #10b981;
}

.timeline-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.timeline-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.timeline-date {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.timeline-status {
  font-size: 12px;
  color: #10b981;
  font-weight: 600;
  background: #dcfce7;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;
  margin-top: 4px;
}

/* Remarks Section */
.remarks-section {
  background: #fffbeb;
  border: 2px solid #fef3c7;
  border-radius: 12px;
  padding: 20px;
}

.remarks-content {
  font-size: 14px;
  color: #78350f;
  line-height: 1.6;
  margin: 0;
}

/* Cancellation Alert */
.cancellation-alert {
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.cancellation-alert > svg {
  color: #dc2626;
  flex-shrink: 0;
}

.alert-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.alert-title {
  font-size: 16px;
  font-weight: 700;
  color: #991b1b;
}

.alert-message {
  font-size: 14px;
  color: #7f1d1d;
  line-height: 1.6;
}

/* Modal Footer */
.custom-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
}

.custom-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.custom-btn.secondary {
  background: #e5e7eb;
  color: #374151;
}

.custom-btn.secondary:hover {
  background: #d1d5db;
  transform: translateY(-1px);
}

.custom-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.custom-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px -4px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .custom-modal-container {
    max-height: 95vh;
    border-radius: 12px;
  }

  .custom-modal-header {
    padding: 20px;
  }

  .header-title {
    font-size: 20px;
  }

  .custom-modal-body {
    padding: 20px;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }

  .progress-stats {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .team-grid {
    grid-template-columns: 1fr;
  }

  .timeline {
    padding-left: 28px;
  }

  .custom-modal-footer {
    flex-direction: column-reverse;
    padding: 16px;
  }

  .custom-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
